import { useCallback, useEffect, useState } from "react"
import { toast } from "@/src/common/snackbar"
import { ConfirmDialog } from "@/src/common/custom-dialog"
import { fetchPromptPolicyVersions, revertPromptPolicyVersion } from "@/src/services/promptPolicies"
import { formatPolicyTypeName, formatTimestamp } from "@/src/features/internalUsers/utils/promptPolicyUtils"
import type { PromptPolicy, PromptPolicyVersion } from "@/src/types/promptPolicy"

import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import CircularProgress from "@mui/material/CircularProgress"
import Dialog from "@mui/material/Dialog"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import IconButton from "@mui/material/IconButton"
import Stack from "@mui/material/Stack"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { Iconify } from "@/src/common/iconify"
import { LoadingButton } from "@mui/lab"
import {
    WorkspacePromptPreviewDialog,
    type PromptPreviewState,
} from "@/src/features/internalUsers/components/WorkspacePromptPreviewDialog"

type PromptPolicyVersionHistoryDialogProps = {
    open: boolean
    policy: PromptPolicy | null
    onClose: () => void
    onSuccess: () => void
}

export function PromptPolicyVersionHistoryDialog({
    open,
    policy,
    onClose,
    onSuccess,
}: PromptPolicyVersionHistoryDialogProps) {
    const [versions, setVersions] = useState<PromptPolicyVersion[]>([])
    const [currentVersionNumber, setCurrentVersionNumber] = useState(0)
    const [isLoading, setIsLoading] = useState(false)
    const [revertTarget, setRevertTarget] = useState<PromptPolicyVersion | null>(null)
    const [versionPreview, setVersionPreview] = useState<PromptPreviewState | null>(null)
    const [isReverting, setIsReverting] = useState(false)
    const [revertConfirmText, setRevertConfirmText] = useState("")

    const REVERT_CONFIRM_PHRASE = "revert"
    const isRevertConfirmed = revertConfirmText === REVERT_CONFIRM_PHRASE

    const loadVersions = useCallback(async () => {
        if (!policy) return
        setIsLoading(true)
        try {
            const data = await fetchPromptPolicyVersions(policy.name)
            setVersions(data.versions)
            setCurrentVersionNumber(data.currentVersionNumber)
        } catch {
            setVersions([])
            setCurrentVersionNumber(0)
        } finally {
            setIsLoading(false)
        }
    }, [policy])

    useEffect(() => {
        if (open && policy) {
            loadVersions()
        }
    }, [open, policy, loadVersions])

    const handleCloseRevertConfirm = () => {
        setRevertTarget(null)
        setRevertConfirmText("")
    }

    const handleClose = () => {
        handleCloseRevertConfirm()
        setVersionPreview(null)
        onClose()
    }

    const handleViewVersion = (version: PromptPolicyVersion) => {
        if (!policy) return
        setVersionPreview({ type: "version", policy, version })
    }

    const handleConfirmRevert = async () => {
        if (!policy || !revertTarget) return
        setIsReverting(true)
        try {
            await revertPromptPolicyVersion(policy.name, revertTarget.versionNumber)
            toast.success(`Reverted global prompt to version ${revertTarget.versionNumber}`)
            handleCloseRevertConfirm()
            onSuccess()
            handleClose()
        } catch {
            // Error toast handled in service
        } finally {
            setIsReverting(false)
        }
    }

    return (
        <>
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
                <DialogTitle sx={{ display: "flex", alignItems: "center", pr: 6 }}>
                    <Box>
                        <Typography variant="h6">Version history</Typography>
                        {policy && (
                            <Typography variant="body2" color="text.secondary">
                                {formatPolicyTypeName(policy.policyType)} — global versions only. Workspace overrides are
                                not changed by revert.
                            </Typography>
                        )}
                    </Box>
                    <IconButton onClick={handleClose} sx={{ position: "absolute", right: 8, top: 8 }}>
                        <Iconify icon="mdi:close" />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {isLoading ? (
                        <Stack alignItems="center" py={4}>
                            <CircularProgress size={32} />
                        </Stack>
                    ) : versions.length === 0 ? (
                        <Typography color="text.secondary" py={2}>
                            No versions found.
                        </Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Version</TableCell>
                                    <TableCell>Created</TableCell>
                                    <TableCell>Created by</TableCell>
                                    <TableCell>Comment</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {versions.map((version) => (
                                    <TableRow key={version.versionNumber} hover>
                                        <TableCell>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Typography variant="body2">v{version.versionNumber}</Typography>
                                                {version.versionNumber === currentVersionNumber && (
                                                    <Typography variant="caption" color="primary">
                                                        (current)
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatTimestamp(version.createdDate)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {version.createdByEmail ?? version.createdBy ?? "—"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ maxWidth: 240 }}>
                                                {version.changeComment ?? "—"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<Iconify icon="mdi:eye-outline" width={16} />}
                                                    onClick={() => handleViewVersion(version)}
                                                >
                                                    View
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={version.versionNumber === currentVersionNumber}
                                                    onClick={() => setRevertTarget(version)}
                                                >
                                                    Revert
                                                </Button>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
            </Dialog>

            <WorkspacePromptPreviewDialog
                preview={versionPreview}
                onClose={() => setVersionPreview(null)}
            />

            <ConfirmDialog
                open={!!revertTarget}
                onClose={handleCloseRevertConfirm}
                title={`Revert to version ${revertTarget?.versionNumber ?? ""}?`}
                maxWidth="sm"
                content={
                    <Stack spacing={2}>
                        <Typography variant="body2">
                            This sets the global active prompt to version {revertTarget?.versionNumber} and deletes
                            all newer global versions. Workspace-specific overrides are not changed.
                        </Typography>
                        {revertTarget && (
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => handleViewVersion(revertTarget)}
                                sx={{ alignSelf: "flex-start", px: 0 }}
                            >
                                View prompt for this version
                            </Button>
                        )}
                        <TextField
                            fullWidth
                            size="small"
                            label={`Type "${REVERT_CONFIRM_PHRASE}" to confirm`}
                            value={revertConfirmText}
                            onChange={(e) => setRevertConfirmText(e.target.value)}
                            autoComplete="off"
                        />
                    </Stack>
                }
                action={
                    <LoadingButton
                        variant="contained"
                        color="warning"
                        loading={isReverting}
                        disabled={!isRevertConfirmed}
                        onClick={handleConfirmRevert}
                    >
                        Revert
                    </LoadingButton>
                }
            />
        </>
    )
}
